import { useState } from 'react';

import { COSMOS_URL } from 'components/globals';
import {
  determinePropertyActionability,
  TOUR_ACTION_TEXT_BUY_DIRECT,
  TOUR_ACTION_TEXT_NON_BUY_DIRECT,
} from 'components/marketplace/helpers/propertyActionability';
import type { PropertyActionabilityInput } from 'components/marketplace/helpers/propertyActionability';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { useObservability } from 'helpers/observability';

interface ActionConfig {
  text: string;
  onClick: () => void;
  href?: string;
  target?: string;
  variant: 'primary' | 'secondary' | 'secondaryInverse';
  disabled?: boolean;
  analyticsName: string;
}

interface UseTouringOptions {
  placement: 'footer' | 'lightbox' | 'main';
}

type TouringProperty = PropertyActionabilityInput & Pick<MarketplaceProperty, 'addressToken'>;

export const useTouring = (property: TouringProperty, options: UseTouringOptions) => {
  const { placement } = options;
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSelfTourDialogOpen, setIsSelfTourDialogOpen] = useState(false);

  const { isActionable, tourKind, showBuyDirect, disableBuyDirect } =
    determinePropertyActionability(property);

  if (!isActionable || !tourKind) {
    return null;
  }

  const buyDirectUrl = `${COSMOS_URL}/checkout/${property.addressToken}`;

  // Analytics naming differs by placement:
  // | Placement      | Analytics Name Pattern                              |
  // |----------------|-----------------------------------------------------|
  // | main           | cosmos-marketplace-{action}-cta                     |
  // | footer/lightbox| cosmos-marketplace-pdp-{placement}-{action}-button  |
  const isMainPlacement = placement === 'main';
  const trackPropertySource = isMainPlacement
    ? 'marketplace-pdp'
    : `marketplace-pdp-${placement}-cta`;
  const journeyEventPlacement = isMainPlacement ? 'above-the-fold' : placement;
  const analyticsNamePrefix = isMainPlacement
    ? 'cosmos-marketplace'
    : `cosmos-marketplace-pdp-${placement}`;
  const analyticsNameSuffix = isMainPlacement ? 'cta' : 'button';

  const handleSelfTour = () => {
    trackProperty('property-self-tour', trackPropertySource);
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: property.addressToken,
      placement: journeyEventPlacement,
      source: 'self-tour',
    });
    setIsSelfTourDialogOpen(true);
  };

  const handleBuyDirect = () => {
    trackProperty('property-buy-direct', trackPropertySource);
    trackJourneyEvent(
      'marketplaceProperty',
      'offerStart',
      {
        addressUuid: property.addressToken,
        source: journeyEventPlacement,
      },
      { flush: true },
    );
  };

  const handleTourWithAgent = () => {
    trackProperty('property-tour-with-agent', trackPropertySource);
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: property.addressToken,
      placement: journeyEventPlacement,
      source: 'tour-with-agent',
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => setIsDialogOpen(false);
  const closeSelfTourDialog = () => setIsSelfTourDialogOpen(false);

  // Determine primary and secondary actions based on property state
  let primaryAction: ActionConfig;
  let secondaryAction: ActionConfig | null = null;

  if (showBuyDirect) {
    // BuyDirect eligible properties show Buy button as primary
    primaryAction = {
      text: disableBuyDirect ? 'Offer pending' : 'Buy this home',
      onClick: handleBuyDirect,
      href: buyDirectUrl,
      target: '_blank',
      variant: 'primary',
      disabled: disableBuyDirect,
      analyticsName: `${analyticsNamePrefix}-buy-direct-${analyticsNameSuffix}`,
    };

    // Show tour as secondary action based on tour kind
    if (tourKind === 'selfTour') {
      secondaryAction = {
        text: TOUR_ACTION_TEXT_BUY_DIRECT.selfTour,
        onClick: handleSelfTour,
        variant: placement === 'lightbox' ? 'secondaryInverse' : 'secondary',
        analyticsName: `${analyticsNamePrefix}-self-tour-${analyticsNameSuffix}`,
      };
    } else if (tourKind === 'tourWithAgent') {
      secondaryAction = {
        text: TOUR_ACTION_TEXT_BUY_DIRECT.tourWithAgent,
        onClick: handleTourWithAgent,
        variant: placement === 'lightbox' ? 'secondaryInverse' : 'secondary',
        analyticsName: `${analyticsNamePrefix}-tour-with-agent-${analyticsNameSuffix}`,
      };
    }
  } else if (tourKind === 'selfTour') {
    primaryAction = {
      text: TOUR_ACTION_TEXT_NON_BUY_DIRECT.selfTour,
      onClick: handleSelfTour,
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'primary',
      analyticsName: `${analyticsNamePrefix}-self-tour-${analyticsNameSuffix}`,
    };
  } else {
    primaryAction = {
      text: TOUR_ACTION_TEXT_NON_BUY_DIRECT.tourWithAgent,
      onClick: handleTourWithAgent,
      variant: placement === 'lightbox' ? 'secondaryInverse' : 'primary',
      analyticsName: `${analyticsNamePrefix}-tour-with-agent-${analyticsNameSuffix}`,
    };
  }

  return {
    primaryAction,
    secondaryAction,
    tourKind,
    showBuyDirect,
    isDialogOpen,
    closeDialog,
    isSelfTourDialogOpen,
    closeSelfTourDialog,
  };
};
